import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Save, DollarSign, Percent, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_NAMES = {
  legal_opinions: 'Legal Opinions',
  ec_extractor: 'EC Extraction',
  builder: 'Builder (BRD)'
};

const ModulePricingConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  const [editedPricing, setEditedPricing] = useState({});
  const [editedDiscounts, setEditedDiscounts] = useState({});
  const [previewModule, setPreviewModule] = useState(null);

  useEffect(() => {
    fetchPricingData();
  }, []);

  const fetchPricingData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/modules/pricing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPricingData(response.data);
      
      // Initialize edited states
      const pricing = {};
      const discounts = {};
      Object.keys(response.data).forEach(module => {
        pricing[module] = response.data[module];
        discounts[module] = response.data[module].discount_tiers || [];
      });
      setEditedPricing(pricing);
      setEditedDiscounts(discounts);
    } catch (error) {
      console.error('Error fetching pricing:', error);
      alert('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const updateModulePricing = async (module) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/modules/pricing`,
        {
          module: module,
          price_per_credit: editedPricing[module].price_per_credit,
          minimum_credits: editedPricing[module].minimum_credits
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert(`Updated pricing for ${MODULE_NAMES[module]}`);
      fetchPricingData();
    } catch (error) {
      console.error('Error updating pricing:', error);
      alert('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const updateDiscountTiers = async (module) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/modules/discounts`,
        {
          module: module,
          tiers: editedDiscounts[module]
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      alert(`Updated discount tiers for ${MODULE_NAMES[module]}`);
      fetchPricingData();
    } catch (error) {
      console.error('Error updating discounts:', error);
      alert('Failed to update discounts');
    } finally {
      setSaving(false);
    }
  };

  const addDiscountTier = (module) => {
    setEditedDiscounts({
      ...editedDiscounts,
      [module]: [
        ...editedDiscounts[module],
        { min_credits: 0, discount_percent: 0 }
      ]
    });
  };

  const removeDiscountTier = (module, index) => {
    setEditedDiscounts({
      ...editedDiscounts,
      [module]: editedDiscounts[module].filter((_, i) => i !== index)
    });
  };

  const updateDiscountTier = (module, index, field, value) => {
    const newTiers = [...editedDiscounts[module]];
    newTiers[index][field] = parseFloat(value) || 0;
    setEditedDiscounts({
      ...editedDiscounts,
      [module]: newTiers
    });
  };

  const calculatePreview = (module, credits) => {
    const pricePerCredit = editedPricing[module]?.price_per_credit || 0;
    const tiers = editedDiscounts[module] || [];
    
    // Find applicable discount
    const sortedTiers = [...tiers].sort((a, b) => b.min_credits - a.min_credits);
    let discountPercent = 0;
    
    for (const tier of sortedTiers) {
      if (credits >= tier.min_credits) {
        discountPercent = tier.discount_percent;
        break;
      }
    }
    
    const baseAmount = credits * pricePerCredit;
    const discountAmount = (baseAmount * discountPercent) / 100;
    const finalAmount = baseAmount - discountAmount;
    
    return {
      baseAmount,
      discountPercent,
      discountAmount,
      finalAmount
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Panel
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Module Pricing & Discounts</h1>
          <p className="text-gray-600 mt-2">Configure pricing and discount tiers for each module</p>
        </div>

        {/* Info Alert */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">💡 What Users Will See</h3>
          <p className="text-sm text-blue-800">
            These settings control what appears in the <strong>Buy Credits</strong> modal for all users. 
            Changes here directly affect pricing, minimum purchase amounts, and discount calculations shown to tenants.
          </p>
        </div>

        {/* Module Cards */}
        <div className="space-y-6">
          {['legal_opinions', 'ec_extractor', 'builder'].map(module => {
            const minCredits = editedPricing[module]?.minimum_credits || 0;
            const previewCalc = calculatePreview(module, minCredits);
            
            return (
            <Card key={module} className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-xl">{MODULE_NAMES[module]}</CardTitle>
                    <CardDescription>Configure all frontend values for this module</CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewModule(previewModule === module ? null : module)}
                  >
                    {previewModule === module ? 'Hide' : 'Show'} Preview
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Live Preview Banner */}
                {previewModule === module && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                      👁️ Live Preview - What Users See
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-white rounded p-3">
                        <div className="text-gray-600 text-xs mb-1">Module Dropdown Display:</div>
                        <div className="font-semibold">{MODULE_NAMES[module]}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          ₹{editedPricing[module]?.price_per_credit?.toLocaleString('en-IN') || 0}/credit • 
                          Min: {editedPricing[module]?.minimum_credits || 0}
                        </div>
                      </div>
                      <div className="bg-white rounded p-3">
                        <div className="text-gray-600 text-xs mb-1">Minimum Purchase Amount:</div>
                        <div className="font-semibold text-lg text-blue-600">
                          ₹{previewCalc.finalAmount.toLocaleString('en-IN')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          For {minCredits} credits (minimum)
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing Section */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Credit Pricing Configuration
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`${module}-price`}>
                        Price per Credit (₹)
                        <span className="text-xs text-gray-500 ml-2">• Shown in module dropdown</span>
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg font-bold text-gray-700">₹</span>
                        <Input
                          id={`${module}-price`}
                          type="number"
                          value={editedPricing[module]?.price_per_credit || 0}
                          onChange={(e) => setEditedPricing({
                            ...editedPricing,
                            [module]: {
                              ...editedPricing[module],
                              price_per_credit: parseFloat(e.target.value) || 0
                            }
                          })}
                          className="flex-1 text-lg font-semibold"
                          placeholder="500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Base price before any discounts
                      </p>
                    </div>
                    <div>
                      <Label htmlFor={`${module}-min`}>
                        Minimum Credits Required
                        <span className="text-xs text-gray-500 ml-2">• Users cannot buy less</span>
                      </Label>
                      <Input
                        id={`${module}-min`}
                        type="number"
                        value={editedPricing[module]?.minimum_credits || 0}
                        onChange={(e) => setEditedPricing({
                          ...editedPricing,
                          [module]: {
                            ...editedPricing[module],
                            minimum_credits: parseInt(e.target.value) || 0
                          }
                        })}
                        className="mt-1 text-lg font-semibold"
                        placeholder="200"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum: ₹{((editedPricing[module]?.minimum_credits || 0) * (editedPricing[module]?.price_per_credit || 0)).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => updateModulePricing(module)}
                  disabled={saving}
                  className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Pricing
                </Button>

                {/* Discount Tiers Section */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      Discount Tiers Configuration
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addDiscountTier(module)}
                      className="border-gray-400 text-gray-700 hover:bg-gray-100"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Tier
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    💡 Discounts are automatically applied when users purchase credits. Higher tiers override lower ones.
                  </p>

                  <div className="space-y-3">
                    {editedDiscounts[module]?.length === 0 && (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <p className="text-gray-500 text-sm">No discount tiers configured</p>
                        <p className="text-xs text-gray-400 mt-1">Click &quot;Add Tier&quot; to create discount rules</p>
                      </div>
                    )}
                    {editedDiscounts[module]?.map((tier, index) => {
                      const tierPreview = calculatePreview(module, tier.min_credits);
                      return (
                        <div key={index} className="border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-4 rounded-lg">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-semibold text-gray-700">
                                  Minimum Credits Threshold
                                </Label>
                                <Input
                                  type="number"
                                  value={tier.min_credits}
                                  onChange={(e) => updateDiscountTier(module, index, 'min_credits', e.target.value)}
                                  className="mt-1 font-semibold"
                                  placeholder="500"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  Applies when user buys ≥ {tier.min_credits} credits
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs font-semibold text-gray-700">
                                  Discount Percentage
                                </Label>
                                <div className="flex items-center gap-2 mt-1">
                                  <Input
                                    type="number"
                                    value={tier.discount_percent}
                                    onChange={(e) => updateDiscountTier(module, index, 'discount_percent', e.target.value)}
                                    className="font-semibold"
                                    placeholder="5"
                                    max="100"
                                    min="0"
                                  />
                                  <Percent className="w-4 h-4 text-gray-400" />
                                </div>
                                <p className="text-xs text-green-600 mt-1 font-medium">
                                  Saves ₹{tierPreview.discountAmount.toLocaleString('en-IN')} at {tier.min_credits} credits
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDiscountTier(module, index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-6"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button 
                    onClick={() => updateDiscountTiers(module)}
                    disabled={saving}
                    className="w-full md:w-auto mt-4 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Discount Tiers
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>

        {/* Summary Section */}
        <Card className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">📊 Current Configuration Summary</CardTitle>
            <CardDescription>Overview of all module pricing visible to users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['legal_opinions', 'ec_extractor', 'builder'].map(module => {
                const config = editedPricing[module] || {};
                const minAmount = (config.minimum_credits || 0) * (config.price_per_credit || 0);
                const discounts = editedDiscounts[module] || [];
                
                return (
                  <div key={module} className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-2">{MODULE_NAMES[module]}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Price/Credit:</span>
                        <span className="font-semibold">₹{config.price_per_credit?.toLocaleString('en-IN') || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min Credits:</span>
                        <span className="font-semibold">{config.minimum_credits || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Min Amount:</span>
                        <span className="font-semibold text-blue-600">₹{minAmount.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="pt-2 border-t">
                        <span className="text-gray-600">Discounts:</span>
                        <div className="mt-1 space-y-1">
                          {discounts.length === 0 ? (
                            <span className="text-xs text-gray-400">No discounts</span>
                          ) : (
                            discounts.map((tier, idx) => (
                              <div key={idx} className="text-xs text-green-600">
                                • {tier.min_credits}+ credits: {tier.discount_percent}% off
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ModulePricingConfig;
